import { useMemo } from 'react';
import type { Draft } from '../components/TenderDetails/types';
import type { User } from '../types/auth';
import type { Tender } from '../types/tender';

import { useSupportDocsDeletion } from './useTenderSupportDocs/useSupportDocsDeletion';
import { useSupportDocsGeneration } from './useTenderSupportDocs/useSupportDocsGeneration';
import { useSupportDocsOverrides } from './useTenderSupportDocs/useSupportDocsOverrides';
import { useSupportDocsSubscriptions } from './useTenderSupportDocs/useSupportDocsSubscriptions';
import { useSupportDocsUpload } from './useTenderSupportDocs/useSupportDocsUpload';

export interface UseTenderSupportDocsProps {
  tenderId: string | undefined;
  tender: Tender | null;
  account: (User & { clientId?: string; actualDefaultBucketName?: string }) | null;
  isAuthLoading: boolean;
  isTenderLoaded: boolean;
  hasPermission: (permission: string) => boolean;
  hasSections?: boolean;
  drafts: Draft[];
  showSnackbar: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void;
  setLoadingStates: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setTender: React.Dispatch<React.SetStateAction<Tender | null>>;
  refreshUserDetails: (silent?: boolean) => void | Promise<void>;
  handleDownload: (
    gsURI: string,
    fileTypeIdentifier: string,
    defaultFileName: string
  ) => Promise<void>;
}

export const useTenderSupportDocs = (props: UseTenderSupportDocsProps) => {
  const {
    tenderId,
    tender,
    account,
    isAuthLoading,
    isTenderLoaded,
    hasPermission,
    hasSections,
    drafts,
    showSnackbar,
    setLoadingStates,
    setTender,
    refreshUserDetails,
    handleDownload,
  } = props;

  // 1. Subscriptions & State
  const {
    user_docs,
    setUser_docs,
    tenderForms,
    setTenderForms,
    tenderCVs,
    setTenderCVs,
    activeLibraryFiles,
    setActiveLibraryFiles,
    documentCategories,
    setDocumentCategories,
  } = useSupportDocsSubscriptions({
    tenderId,
    tender,
    account,
    isAuthLoading,
    isTenderLoaded,
    hasPermission,
    showSnackbar,
  });

  const sortedDocs = useMemo(
    () =>
      [...user_docs, ...drafts, ...tenderForms].sort((a, b) => {
        const timeA = a.uploadedAt ? new Date(a.uploadedAt as string | number | Date).getTime() : 0;
        const timeB = b.uploadedAt ? new Date(b.uploadedAt as string | number | Date).getTime() : 0;
        return timeB - timeA;
      }),
    [user_docs, drafts, tenderForms]
  );

  // 2. Uploads
  const {
    processingFiles,
    setProcessingFiles,
    uploadValidationWarning,
    isDraggingAdhoc,
    isDraggingReview,
    isDraggingForm,
    handleFileUpload,
    commonDragEvents,
    handleDragEventsReview,
    handleDropReview,
    handleDragEventsAdhoc,
    handleDropAdhoc,
    handleDragEventsForm,
    handleDropForm,
  } = useSupportDocsUpload({
    tender,
    account,
    showSnackbar,
    setLoadingStates,
    setTender,
    setDocumentCategories,
  });

  // 3. Deletions
  const { handleDeleteSupportDoc, handleDeleteTenderForm } = useSupportDocsDeletion({
    tender,
    account,
    showSnackbar,
    setLoadingStates,
    setUser_docs,
    tenderCVs,
  });

  // 4. Overrides (Category Management)
  const { handleCategoryChange } = useSupportDocsOverrides({
    tender,
    account,
    showSnackbar,
    sortedDocs,
    tenderCVs,
    setDocumentCategories,
  });

  // 5. Generation (Adhoc & Downloads)
  const { handleGenerateAdHocDocument, handleDownloadFormInput, handleDownloadFilledForm } =
    useSupportDocsGeneration({
      tender,
      account,
      showSnackbar,
      hasSections,
      setLoadingStates,
      setTenderForms,
      refreshUserDetails,
      handleDownload,
    });

  return {
    user_docs,
    tenderForms,
    tenderCVs,
    activeLibraryFiles,
    documentCategories,
    processingFiles,
    uploadValidationWarning,
    isDraggingAdhoc,
    isDraggingReview,
    isDraggingForm,
    sortedDocs,
    setTenderCVs,
    setProcessingFiles,
    setDocumentCategories,
    setActiveLibraryFiles,
    handleFileUpload,
    commonDragEvents,
    handleDragEventsReview,
    handleDropReview,
    handleDragEventsAdhoc,
    handleDropAdhoc,
    handleDragEventsForm,
    handleDropForm,
    handleDeleteSupportDoc,
    handleDeleteTenderForm,
    handleGenerateAdHocDocument,
    handleDownloadFormInput,
    handleDownloadFilledForm,
    handleCategoryChange,
  };
};
